import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleAPIProvider } from "@jti/fe-clients";
import { clearShippingSelectedAddressId, selectAddresses, selectPostCodeConfig, setShippingSelectedAddressId, toggleSelectShippingAddress, useDispatch, useSelector, } from "@jti/store";
import { AddressFormProvider, AddressInfoBlock, Button, DEPRECATED_Card, MessageBox, MessageBoxVariant, useConfig, useLocale, useTranslations, } from "@jti/ui";
import { OrderAttributes } from "../../../../components/OrderAttributes";
import { isInvalidPostCode } from "../../../../utils";
import { defaultShippingAddress } from "../../defaultShippingFormValues";
import { useShippingFormContext } from "../../hooks/useShippingFormContext";
import { SelectShippingAddressDialog } from "../SelectShippingAddressDialog";
import styles from "./ShippingAddress.module.scss";
export const ShippingAddress = ({ orderDeliveryLimits }) => {
    const locale = useLocale();
    const dispatch = useDispatch();
    const { __ } = useTranslations();
    const config = useConfig();
    const postCodeRef = useRef("");
    const postCodeConfig = useSelector(selectPostCodeConfig);
    const addresses = useSelector(selectAddresses);
    const country = useSelector((state) => state.checkout.country);
    const formContext = useShippingFormContext();
    const { errors } = formContext.formState;
    const atHomeDeliveryShippingAddress = formContext.watch("atHomeDeliveryShippingAddress");
    const [mode, setMode] = useState(atHomeDeliveryShippingAddress ? "info" : "new");
    const setCountryFieldsValue = useCallback(() => {
        Object.entries(country).forEach(([key, value]) => {
            formContext.setValue(`atHomeDeliveryShippingAddress.${key}`, value);
        });
    }, [country]);
    const showShippingAddressSelector = useCallback(() => {
        dispatch(toggleSelectShippingAddress(true));
    }, [dispatch]);
    const onCreateNewAddress = useCallback(() => {
        setMode("new");
        formContext.setValue("atHomeDeliveryShippingAddress", defaultShippingAddress);
        dispatch(clearShippingSelectedAddressId());
        setCountryFieldsValue();
        formContext.clearErrors("atHomeDeliveryShippingAddress");
    }, [setMode, setCountryFieldsValue, formContext.clearErrors]);
    const onSelectExistingAddress = useCallback((addressId) => {
        const selectedAddress = addresses.find((address) => address.id === addressId);
        if (!selectedAddress) {
            return;
        }
        formContext.setValue("atHomeDeliveryShippingAddress", selectedAddress);
        dispatch(setShippingSelectedAddressId(addressId));
        setMode("info");
    }, [setMode, addresses]);
    useEffect(() => {
        const shouldSetPostCodeError = mode === "info" && postCodeConfig && isInvalidPostCode(atHomeDeliveryShippingAddress?.postcode, postCodeConfig);
        if (!shouldSetPostCodeError) {
            formContext.clearErrors("atHomeDeliveryShippingAddress.postcode");
            return;
        }
        formContext.setError("atHomeDeliveryShippingAddress.postcode", {
            type: "manual",
            message: __("form_validation.postcode_from_account_incorrect"),
        });
        formContext.trigger();
    }, [atHomeDeliveryShippingAddress?.postcode, formContext.setError, formContext.clearErrors, mode, postCodeConfig]);
    useEffect(function clearServerErrors() {
        if (postCodeRef.current !== atHomeDeliveryShippingAddress?.postcode && errors.root) {
            formContext.clearErrors("root");
        }
        postCodeRef.current = atHomeDeliveryShippingAddress?.postcode;
    }, [mode, formContext.clearErrors, atHomeDeliveryShippingAddress?.postcode, errors.root]);
    useEffect(() => {
        if (mode === "info" || (mode === "new" && atHomeDeliveryShippingAddress)) {
            return;
        }
        setCountryFieldsValue();
    }, [mode, atHomeDeliveryShippingAddress, setCountryFieldsValue]);
    return (_jsxs(_Fragment, { children: [_jsxs(DEPRECATED_Card, { titleDataTestId: "shipping-address-card", title: `${__("cart.checkout.shipping_address.title")} ${mode === "new" ? "*" : ""} `, variant: "variant4", "data-testid": "shipping-address", children: [_jsx(OrderAttributes, { target: "shippingAddress_before" }), errors.root?.shippingAddress ? (_jsx(MessageBox, { className: styles.messageBox, variant: MessageBoxVariant.warning, "data-testid": "magento-shipping-error", isVisible: true, isCompact: true, children: errors.root.shippingAddress.message })) : null, errors.atHomeDeliveryShippingAddress?.postcode?.type === "manual" ? (_jsx(MessageBox, { className: styles.messageBox, variant: MessageBoxVariant.warning, "data-testid": "postcode-error-message-box", isCompact: true, isVisible: true, children: errors.atHomeDeliveryShippingAddress.postcode.message })) : null, mode === "new" && (_jsx(GoogleAPIProvider, { apiKey: config.GOOGLE_MAPS_API, options: {
                            version: "weekly",
                            libraries: ["geometry", "places"],
                            language: locale,
                            region: config.ISO_COUNTRY_CODE,
                        }, children: _jsx(AddressFormProvider, { "data-testid": "shipping-address-field", orderDeliveryLimits: orderDeliveryLimits, locationServiceAutocompleteProvider: config.locationServiceAutocompleteProvider, formType: config.addressFormVariant, fieldName: (name) => `atHomeDeliveryShippingAddress.${name}`, postCodeConfig: postCodeConfig }) })), mode === "info" && atHomeDeliveryShippingAddress && (_jsx(AddressInfoBlock, { address: atHomeDeliveryShippingAddress, hideName: true, hideTelephone: true, isEditable: true, editLabel: __("cart.checkout.shipping_address.edit"), onEdit: showShippingAddressSelector })), _jsx(OrderAttributes, { target: "shippingAddress_after" }), mode === "new" && (_jsx("div", { className: styles.bottomAction, children: _jsx(Button, { color: "tertiary", onClick: showShippingAddressSelector, "data-testid": "bottomActionButton", children: __("cart.checkout.shipping_address.edit") }) }))] }), _jsx(SelectShippingAddressDialog, { onCreateAddress: onCreateNewAddress, onSelectAddress: onSelectExistingAddress, defaultSelectedAddressId: atHomeDeliveryShippingAddress?.id })] }));
};
//# sourceMappingURL=ShippingAddress.js.map